/**
 * Bridge between the window-level error handlers and the live toast layer.
 *
 * `src/main.tsx` installs the `error` / `unhandledrejection` listeners BEFORE
 * React mounts, so nothing that throws during boot is lost. Those listeners
 * can only log until something can render, though: this component (mounted
 * inside ToastProvider) hands them the live `toast` function plus the copy in
 * the active locale, and takes it away again on unmount so a stale setter can
 * never fire into a torn-down tree.
 *
 * Renders nothing. It exists purely for the effect.
 */
import { useEffect } from "react";
import { setGlobalToastDispatcher } from "../utils/globalErrorHandlers";
import { useT, type Locale } from "./i18n";
import { useToast } from "./Toast";

interface Copy {
  unexpected: string;
  rejection: string;
  copyReport: string;
  copyFailed: string;
}

/**
 * Deliberately plain: an escaped error is not the moment to explain the
 * architecture. Say that something failed, say the data stayed local, offer
 * the report.
 */
const COPY: Record<Locale, Copy> = {
  en: {
    unexpected: "Something went wrong. Nothing left this device.",
    rejection: "A background task failed. Nothing left this device.",
    copyReport: "Copy report",
    copyFailed: "The report was not copied; this browser refused the clipboard.",
  },
  de: {
    unexpected: "Etwas ist schiefgelaufen. Nichts hat dieses Gerät verlassen.",
    rejection:
      "Eine Aufgabe im Hintergrund ist fehlgeschlagen. Nichts hat dieses Gerät verlassen.",
    copyReport: "Bericht kopieren",
    copyFailed:
      "Der Bericht wurde nicht kopiert; dieser Browser hat die Zwischenablage verweigert.",
  },
};

export function GlobalErrorToasts() {
  const { locale } = useT();
  const { toast } = useToast();

  useEffect(() => {
    const copy = COPY[locale] ?? COPY.en;
    setGlobalToastDispatcher({ toast, copy });
    return () => setGlobalToastDispatcher(null);
  }, [locale, toast]);

  return null;
}
